import type { NotificationResponse } from "@repo/share-log-interfaces";
import type { StateStatus, StoreDeps } from "../types";
import type { ImmerSet } from "../helpers/create-store";
import { createStore } from "../helpers/create-store";
import { withFetchStatus } from "../helpers/with-fetch-status";
import { createReset } from "../helpers/create-reset";

interface NotificationState {
  notifications: NotificationResponse[];
  hasMore: boolean;
  nextCursor: string | undefined;
  hasUnread: boolean;
  status: StateStatus;
  loadMoreStatus: StateStatus;
}

interface NotificationActions {
  fetchNotifications: () => Promise<void>;
  loadMore: () => Promise<void>;
  checkUnread: () => Promise<void>;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  reset: () => void;
}

export type NotificationStore = NotificationState & NotificationActions;

const initialState: NotificationState = {
  notifications: [],
  hasMore: false,
  nextCursor: undefined,
  hasUnread: false,
  status: "idle",
  loadMoreStatus: "idle",
};

export function createNotificationStore(deps: StoreDeps) {
  return createStore<NotificationStore>("NotificationStore", (set, get) => ({
    ...initialState,

    fetchNotifications: () =>
      withFetchStatus(
        (s) => set((state) => { state.status = s; }),
        deps.logger,
        async () => {
          const result = await deps.api.notification.list();
          set((state) => {
            state.notifications = result.notifications;
            state.hasMore = result.hasMore;
            state.nextCursor = result.nextCursor;
          });
        },
      ),

    loadMore: () => {
      const { hasMore, loadMoreStatus, nextCursor } = get();
      if (!hasMore || loadMoreStatus === "loading") return Promise.resolve();

      return withFetchStatus(
        (s) => set((state) => { state.loadMoreStatus = s; }),
        deps.logger,
        async () => {
          const result = await deps.api.notification.list(nextCursor);
          set((state) => {
            state.notifications = [...state.notifications, ...result.notifications];
            state.hasMore = result.hasMore;
            state.nextCursor = result.nextCursor;
          });
        },
      );
    },

    checkUnread: async () => {
      try {
        const result = await deps.api.notification.checkUnread();
        set((state) => { state.hasUnread = result.hasUnread; });
      } catch (error) {
        deps.logger.error(error);
      }
    },

    markAsRead: async (id) => {
      await deps.api.notification.markAsRead(id);
      set((state) => {
        const notification = state.notifications.find((n) => n.id === id);
        if (notification) notification.isRead = true;
      });
      void get().checkUnread();
    },

    markAllAsRead: async () => {
      await deps.api.notification.markAllAsRead();
      set((state) => {
        state.notifications.forEach((n) => { n.isRead = true; });
        state.hasUnread = false;
      });
    },

    reset: createReset(set as ImmerSet<NotificationState>, initialState),
  }));
}
